import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Factory, Building2, Home, CheckCircle2, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";

const sectors = [
  { id: "industry", label: "Industrie", icon: Factory, desc: "Production, procédés, utilités" },
  { id: "commercial", label: "Tertiaire & Commerce", icon: Building2, desc: "Bureaux, retail, hôtellerie, santé" },
  { id: "residential", label: "Résidentiel", icon: Home, desc: "Maisons individuelles & collectif" },
];

const projectTypes = {
  industry: ["Récupération de chaleur fatale", "Réfrigération", "Air comprimé", "Chaudières / vapeur", "Moteurs / pompes / variation", "Éclairage", "Audit / monitoring", "Autre"],
  commercial: ["CVC", "Réfrigération", "Chauffage", "Éclairage", "GTB / régulation", "Isolation / enveloppe", "Audit", "Autre"],
  residential: ["Isolation", "Pompe à chaleur", "Chauffe-eau thermodynamique", "Ventilation (VMC)", "Système de chauffage", "Rénovation globale", "Autre"],
};

export default function EligibilityWizard() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    sector: "",
    project_type: "",
    full_name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const total = 3;
  const progress = ((step + 1) / (total + 1)) * 100;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, total));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canContinue =
    (step === 0 && data.sector) ||
    (step === 1 && data.project_type) ||
    step === 2;

  const onSubmit = async () => {
    if (!data.full_name || !data.email) {
      toast.error("Merci de renseigner votre nom et votre e-mail.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        ...data,
        sector: data.sector || "unknown",
        source: "eligibility",
      });
      setDone(true);
      toast.success("Demande bien reçue — un expert vous répond sous 48 h.");
    } catch (e) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="eligibility-success" className="bg-white border border-[#E5E7EB] rounded-2xl p-10 md:p-14 text-center shadow-sm">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#10B981]/10 grid place-items-center text-[#10B981] mb-6">
          <CheckCircle2 className="h-9 w-9" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1F2937] mb-3">
          Demande bien reçue — merci.
        </h3>
        <p className="text-[#4B5563] max-w-xl mx-auto">
          Un expert Énergia CEE prépare votre analyse d&apos;éligibilité personnalisée.
          Vous recevrez une réponse détaillée sous 48 heures.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="eligibility-wizard" className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm overflow-hidden">
      <div className="px-6 md:px-10 pt-8 md:pt-10">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[#4B5563] mb-3">
          <span data-testid="eligibility-step-label">Étape {step + 1} sur {total + 1}</span>
          <span className="text-[#10B981] font-semibold">Gratuit · 2 min</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-[#E5E7EB]" />
      </div>

      <div className="px-6 md:px-10 py-10 min-h-[420px]">
        {step === 0 && (
          <div data-testid="step-sector" className="space-y-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1F2937]">Quel secteur décrit le mieux votre projet ?</h3>
              <p className="text-[#4B5563] mt-2">Nous adaptons l&apos;analyse à votre contexte.</p>
            </div>
            <RadioGroup value={data.sector} onValueChange={(v) => update("sector", v)} className="grid sm:grid-cols-3 gap-4">
              {sectors.map((s) => (
                <label
                  key={s.id}
                  data-testid={`sector-option-${s.id}`}
                  className={`cursor-pointer rounded-xl border-2 p-5 transition-all hover:border-[#10B981] hover:shadow-sm ${
                    data.sector === s.id ? "border-[#10B981] bg-[#10B981]/5" : "border-[#E5E7EB]"
                  }`}
                >
                  <RadioGroupItem value={s.id} className="sr-only" />
                  <s.icon className={`h-7 w-7 mb-3 ${data.sector === s.id ? "text-[#10B981]" : "text-[#1F2937]"}`} strokeWidth={1.75} />
                  <div className="font-semibold text-[#1F2937]">{s.label}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{s.desc}</div>
                </label>
              ))}
            </RadioGroup>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-project" className="space-y-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1F2937]">Quel type de projet envisagez-vous ?</h3>
              <p className="text-[#4B5563] mt-2">Choisissez le plus proche — vous pourrez préciser ensuite.</p>
            </div>
            <Select value={data.project_type} onValueChange={(v) => update("project_type", v)}>
              <SelectTrigger data-testid="project-type-select" className="h-14 text-base">
                <SelectValue placeholder="Sélectionnez un type de projet" />
              </SelectTrigger>
              <SelectContent>
                {(projectTypes[data.sector] || projectTypes.industry).map((p) => (
                  <SelectItem key={p} value={p} data-testid={`project-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <Label htmlFor="message" className="text-[#1F2937]">Une précision à nous partager ? (facultatif)</Label>
              <Textarea
                id="message"
                data-testid="wizard-message"
                value={data.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Taille du site, équipements actuels, calendrier visé..."
                className="mt-2"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-contact" className="space-y-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1F2937]">Où devons-nous envoyer votre analyse ?</h3>
              <p className="text-[#4B5563] mt-2">Aucun spam, jamais. Réponse sous 48 heures.</p>
              <span className="sr-only">eligibility</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name" className="text-[#1F2937]">Nom complet *</Label>
                <Input id="full_name" data-testid="wizard-name" value={data.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-2 h-12" required />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#1F2937]">E-mail *</Label>
                <Input id="email" type="email" data-testid="wizard-email" value={data.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12" required />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#1F2937]">Téléphone</Label>
                <Input id="phone" data-testid="wizard-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12" />
              </div>
              <div>
                <Label htmlFor="company" className="text-[#1F2937]">Entreprise / Bien</Label>
                <Input id="company" data-testid="wizard-company" value={data.company} onChange={(e) => update("company", e.target.value)} className="mt-2 h-12" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] px-6 md:px-10 py-5 flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={back}
          disabled={step === 0}
          data-testid="wizard-back"
          className="text-[#1F2937]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
        {step < 2 ? (
          <Button
            type="button"
            onClick={next}
            disabled={!canContinue}
            data-testid="wizard-next"
            className="bg-[#10B981] hover:bg-[#059669] text-white font-semibold px-7 py-3"
          >
            Continuer <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            data-testid="wizard-submit"
            className="bg-[#0055A4] hover:bg-[#003D7A] text-white font-semibold px-7 py-3 shadow-md"
          >
            {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi</>) : "Recevoir mon analyse d'éligibilité"}
          </Button>
        )}
      </div>
    </div>
  );
}
